import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Loader2, Award, Lightbulb, Target, Share2, Linkedin, Mail, Copy, Check, Star, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { api } from '../App';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { trackCourseCompleted, trackPDFDownload } from '../utils/analytics';
import confetti from 'canvas-confetti';

const LOADING_MESSAGES = [
  "Preparing your summary...",
  "Gathering your insights...",
  "Reviewing your 6-week journey...",
  "Analysing your reflections...",
  "Compiling your personalised report...",
  "Almost there...",
];

const NPS_LABELS = {
  0: "Not at all likely",
  5: "Neutral",
  10: "Extremely likely"
};

const CompletionPage = ({ user }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloadingCert, setDownloadingCert] = useState(false);
  const [downloadingCourse, setDownloadingCourse] = useState(false);
  const [copied, setCopied] = useState(false);
  const [hasTrackedCompletion, setHasTrackedCompletion] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const [npsScore, setNpsScore] = useState(null);
  const [npsSubmitted, setNpsSubmitted] = useState(false);
  const [npsComment, setNpsComment] = useState('');

  // Rotate loading messages
  useEffect(() => {
    if (!loading) return;
    const interval = setInterval(() => {
      setLoadingMessageIndex(prev => (prev + 1) % LOADING_MESSAGES.length);
    }, 2500);
    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    // Wait a brief moment for App.js to load user from localStorage
    const timer = setTimeout(() => {
      setInitialCheckDone(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!initialCheckDone) return;
    
    if (!user) {
      navigate('/');
      return;
    }

    const fetchData = async () => {
      try {
        // Fetch summary
        const response = await api.get(`/users/${user.id}/completion-summary`);
        setSummary(response.data);
        
        // Trigger confetti celebration (only once per session)
        if (!sessionStorage.getItem('completion_confetti_shown')) {
          confetti({
            particleCount: 150,
            spread: 100,
            origin: { y: 0.6 }
          });
          // Fire a second burst for extra celebration
          setTimeout(() => {
            confetti({
              particleCount: 100,
              spread: 120,
              origin: { y: 0.5, x: 0.3 }
            });
            confetti({
              particleCount: 100,
              spread: 120,
              origin: { y: 0.5, x: 0.7 }
            });
          }, 300);
          sessionStorage.setItem('completion_confetti_shown', 'true');
        }
        
        // Trigger completion email (backend will check if not already sent)
        try {
          await api.post(`/users/${user.id}/send-completion-email`);
        } catch (emailError) {
          // Silently fail - email might already be sent
          console.log('Completion email:', emailError.response?.data?.message || 'already sent or error');
        }
        
        // Check if NPS already submitted
        const npsCheck = await api.get(`/nps/check/${user.id}`);
        if (npsCheck.data.submitted) {
          setNpsSubmitted(true);
          setNpsScore(npsCheck.data.score);
        }
        
        // Track course completion (only once)
        if (!hasTrackedCompletion && response.data) {
          const expectedActivities = (user.weekly_commitment / 60) * 6;
          const actualActivities = response.data.total_responses;
          const completionType = actualActivities >= expectedActivities ? 'full' : 'partial';
          trackCourseCompleted(actualActivities, expectedActivities, completionType);
          setHasTrackedCompletion(true);
        }
      } catch (error) {
        console.error('Error fetching completion summary:', error);
        toast.error('Failed to load completion summary');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, initialCheckDone]);

  const downloadCertificate = async () => {
    if (!summary) return;
    setDownloadingCert(true);

    try {
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Background gradient effect (light blue to white)
      pdf.setFillColor(240, 248, 255);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      // Border
      pdf.setDrawColor(37, 99, 235);
      pdf.setLineWidth(2);
      pdf.rect(10, 10, pageWidth - 20, pageHeight - 20);

      // Inner decorative border
      pdf.setLineWidth(0.5);
      pdf.rect(15, 15, pageWidth - 30, pageHeight - 30);

      // Header - KODO logo text
      pdf.setTextColor(37, 99, 235);
      pdf.setFontSize(28);
      pdf.setFont('helvetica', 'bold');
      pdf.text('KODO', pageWidth / 2, 35, { align: 'center' });

      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text('6-Week Career Transformation Programme', pageWidth / 2, 43, { align: 'center' });

      // Certificate title
      pdf.setFontSize(36);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 30, 30);
      pdf.text('Certificate of Completion', pageWidth / 2, 70, { align: 'center' });

      // Decorative line
      pdf.setDrawColor(37, 99, 235);
      pdf.setLineWidth(1);
      pdf.line(pageWidth / 2 - 60, 78, pageWidth / 2 + 60, 78);

      // This certifies text
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text('This is to certify that', pageWidth / 2, 95, { align: 'center' });

      // User name
      pdf.setFontSize(32);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(37, 99, 235);
      pdf.text(summary.user_name || user?.name || 'Graduate', pageWidth / 2, 115, { align: 'center' });

      // Completion text
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(80, 80, 80);
      pdf.text('has successfully completed the', pageWidth / 2, 130, { align: 'center' });

      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 30, 30);
      pdf.text('Kodo 6-Week Career Transformation Programme', pageWidth / 2, 142, { align: 'center' });

      // Stats
      pdf.setFontSize(11);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      pdf.text(`${summary.weeks_completed} Weeks  •  ${summary.total_responses} Activities`, pageWidth / 2, 155, { align: 'center' });

      // Date
      const completionDate = new Date().toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
      pdf.setFontSize(12);
      pdf.text(`Completed on ${completionDate}`, pageWidth / 2, 175, { align: 'center' });

      pdf.save('Kodo_Certificate.pdf');
      trackPDFDownload('certificate');
      toast.success('Certificate downloaded!');
    } catch (error) {
      console.error('Error generating certificate:', error);
      toast.error('Failed to generate certificate');
    } finally {
      setDownloadingCert(false);
    }
  };

  const downloadFullCourse = async () => {
    if (!user) return;
    setDownloadingCourse(true);

    try {
      const response = await api.get(`/users/${user.id}/full-course-pdf`);
      const courseData = response.data;

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;

      // Helper function to sanitize text for PDF rendering
      const sanitizeText = (text) => {
        if (!text) return '';
        // Only replace problematic Unicode characters, preserve line breaks
        return text
          .replace(/[\u2018\u2019]/g, "'")   // Smart single quotes to regular
          .replace(/[\u201C\u201D]/g, '"')   // Smart double quotes to regular
          .replace(/\u2013/g, '-')           // En dash
          .replace(/\u2014/g, '--')          // Em dash
          .replace(/\u2026/g, '...')         // Ellipsis
          .replace(/\u00A0/g, ' ')           // Non-breaking space
          .replace(/\u2022/g, '*')           // Bullet point
          .replace(/\u2192/g, '->')          // Arrow
          .replace(/\u2190/g, '<-');         // Left arrow
      };

      const addNewPage = () => {
        pdf.addPage();
        yPos = margin;
      };

      const checkPageBreak = (neededHeight) => {
        if (yPos + neededHeight > pageHeight - margin) {
          addNewPage();
          return true;
        }
        return false;
      };

      // Title page
      pdf.setFillColor(37, 99, 235);
      pdf.rect(0, 0, pageWidth, 80, 'F');

      pdf.setTextColor(255, 255, 255);
      pdf.setFontSize(32);
      pdf.setFont('helvetica', 'bold');
      pdf.text('KODO', pageWidth / 2, 35, { align: 'center' });

      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'normal');
      pdf.text('6-Week Career Transformation Programme', pageWidth / 2, 48, { align: 'center' });

      pdf.setFontSize(18);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Your Complete Journey', pageWidth / 2, 68, { align: 'center' });

      pdf.setTextColor(30, 30, 30);
      pdf.setFontSize(16);
      yPos = 100;
      pdf.text(`Prepared for: ${courseData.user_name}`, margin, yPos);

      yPos += 10;
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(100, 100, 100);
      const genDate = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
      pdf.text(`Generated on ${genDate}`, margin, yPos);

      // Add each week
      for (const week of courseData.weeks) {
        addNewPage();

        // Week header
        pdf.setFillColor(37, 99, 235);
        pdf.rect(0, 0, pageWidth, 25, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Week ${week.week_number}: ${week.title}`, margin, 17);

        yPos = 35;
        pdf.setTextColor(80, 80, 80);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        const descLines = pdf.splitTextToSize(week.description, contentWidth);
        pdf.text(descLines, margin, yPos);
        yPos += descLines.length * 5 + 10;

        // Activities
        for (const activity of week.activities) {
          if (!activity.user_response) continue;

          checkPageBreak(60);

          // Activity title
          pdf.setFontSize(12);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(37, 99, 235);
          pdf.text(activity.title, margin, yPos);
          yPos += 7;

          // Activity description
          pdf.setFontSize(9);
          pdf.setFont('helvetica', 'italic');
          pdf.setTextColor(100, 100, 100);
          const promptLines = pdf.splitTextToSize(sanitizeText(activity.prompt), contentWidth);
          pdf.text(promptLines, margin, yPos);
          yPos += promptLines.length * 4 + 5;

          checkPageBreak(30);

          // User response
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(30, 30, 30);
          pdf.text('Your Response:', margin, yPos);
          yPos += 5;

          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(50, 50, 50);
          const responseLines = pdf.splitTextToSize(sanitizeText(activity.user_response), contentWidth);
          
          // Print response with page breaks as needed
          for (let i = 0; i < responseLines.length; i++) {
            if (yPos > pageHeight - margin - 10) {
              addNewPage();
            }
            pdf.text(responseLines[i], margin, yPos);
            yPos += 4;
          }
          yPos += 5;

          // AI insight (if available)
          if (activity.ai_insight) {
            checkPageBreak(30);

            pdf.setFontSize(10);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(234, 88, 12);
            pdf.text('AI Insight:', margin, yPos);
            yPos += 5;

            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(80, 80, 80);
            const insightLines = pdf.splitTextToSize(sanitizeText(activity.ai_insight), contentWidth);
            
            // Print full insight with page breaks as needed
            for (let i = 0; i < insightLines.length; i++) {
              if (yPos > pageHeight - margin - 10) {
                addNewPage();
              }
              pdf.text(insightLines[i], margin, yPos);
              yPos += 4;
            }
            yPos += 10;
          }

          yPos += 5;
        }
      }

      pdf.save('Kodo_Full_Course.pdf');
      trackPDFDownload('full_programme');
      toast.success('Full course PDF downloaded!');
    } catch (error) {
      console.error('Error generating full course PDF:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setDownloadingCourse(false);
    }
  };

  const shareOnLinkedIn = () => {
    const text = `I've just completed the Kodo 6-Week Career Transformation Programme! 🎉\n\nAfter 6 weeks of deep reflection and guided exercises, I've gained clarity on my career direction and what truly matters to me.\n\n#CareerDevelopment #PersonalGrowth #Kodo`;
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.origin)}&summary=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  const shareViaEmail = () => {
    const subject = 'I completed the Kodo Career Programme!';
    const body = `Hi,\n\nI wanted to share that I've just completed the Kodo 6-Week Career Transformation Programme!\n\nIt's been an incredible journey of self-discovery, and I've gained real clarity on my career direction.\n\nCheck it out: ${window.location.origin}\n\nBest wishes`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const copyToClipboard = () => {
    const text = `I've just completed the Kodo 6-Week Career Transformation Programme! 🎉 After 6 weeks of reflection and guided exercises, I've gained clarity on my career direction. Check it out: ${window.location.origin}`;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const submitNPS = async () => {
    if (npsScore === null) return;
    
    try {
      await api.post('/nps/submit', {
        user_id: user.id,
        score: npsScore,
        comment: npsComment
      });
      setNpsSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      console.error('Error submitting NPS:', error);
      toast.error('Failed to submit feedback');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-orange-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600 transition-opacity duration-300">{LOADING_MESSAGES[loadingMessageIndex]}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-100">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-4">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/dashboard')}
              data-testid="back-to-dashboard-button"
              variant="ghost"
              className="rounded-full"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-xl font-bold text-slate-900">
              Programme Completion
            </h1>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
        <div className="max-w-3xl mx-auto space-y-6">

          {/* Congratulations Section */}
          <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl p-8 text-white text-center animate-fade-in" data-testid="congratulations-section">
            <Award className="w-14 h-14 mx-auto mb-4" />
            <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
              You Did It! 🎉
            </h2>
            <p className="text-lg text-blue-100 max-w-xl mx-auto">
              Congratulations, {summary?.user_name?.split(' ')[0] || user?.name?.split(' ')[0] || 'there'} — you've put real thought and disciplined effort into this transition at a major life moment.
            </p>
          </div>

          {/* Key Insights */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="key-insights-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Lightbulb className="w-5 h-5 text-blue-600" />
              </div>
              <h3 className="font-heading text-xl font-bold text-slate-900">Key Insights</h3>
            </div>
            <ul className="space-y-3">
              {(summary?.key_insights || []).map((insight, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">
                    {index + 1}
                  </span>
                  <span className="text-slate-700">{insight}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Key Themes */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="key-themes-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Target className="w-5 h-5 text-orange-600" />
              </div>
              <h3 className="font-heading text-xl font-bold text-slate-900">Key Themes</h3>
            </div>
            <ul className="space-y-3">
              {(summary?.key_themes || []).map((theme, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 bg-orange-50 text-orange-600 rounded-full flex items-center justify-center text-sm font-semibold">
                    {index + 1}
                  </span>
                  <span className="text-slate-700">{theme}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Next Steps */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="next-steps-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Lightbulb className="w-5 h-5 text-purple-600" />
              </div>
              <h3 className="font-heading text-xl font-bold text-slate-900">Next Steps</h3>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-100">
              <h4 className="font-semibold text-slate-900 mb-3">Future Self Exercise (10 minutes)</h4>
              <p className="text-slate-700 mb-4">
                Take a moment to imagine your life in 6 months. Close your eyes and picture yourself having made progress on your career transition.
              </p>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  <span>What will you be doing day-to-day?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  <span>How will you feel about your work?</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 mt-1">•</span>
                  <span>What will have changed in your life?</span>
                </li>
              </ul>
              <p className="text-slate-600 mt-4 text-sm italic">
                Write down what you see. Revisit this vision regularly to stay connected to your goals.
              </p>
            </div>
          </div>

          {/* Downloads */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="downloads-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-green-100 rounded-lg">
                <Download className="w-5 h-5 text-green-600" />
              </div>
              <h3 className="font-heading text-xl font-bold text-slate-900">Download</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Button
                onClick={downloadFullCourse}
                disabled={downloadingCourse}
                data-testid="download-full-course-button"
                className="h-auto py-4 px-6 bg-primary hover:bg-primary/90 text-white rounded-xl flex flex-col items-center gap-2"
              >
                {downloadingCourse ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <Download className="w-6 h-6" />
                )}
                <span className="font-semibold">Full Programme Record</span>
                <span className="text-xs text-blue-200">All responses & AI insights</span>
              </Button>

              <Button
                onClick={downloadCertificate}
                disabled={downloadingCert}
                data-testid="download-certificate-button"
                variant="outline"
                className="h-auto py-4 px-6 border-2 border-slate-200 hover:border-primary hover:bg-blue-50 rounded-xl flex flex-col items-center gap-2"
              >
                {downloadingCert ? (
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                ) : (
                  <Award className="w-6 h-6 text-primary" />
                )}
                <span className="font-semibold text-slate-900">Completion Certificate</span>
                <span className="text-xs text-slate-500">PDF • Shareable</span>
              </Button>
            </div>
          </div>

          {/* NPS Survey */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100" data-testid="nps-section">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <Star className="w-5 h-5 text-yellow-600" />
              </div>
              <h3 className="font-heading text-xl font-bold text-slate-900">Help Us Improve</h3>
            </div>
            
            {npsSubmitted ? (
              <div className="text-center py-6">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                <p className="text-lg font-semibold text-slate-900">Thank you for your feedback!</p>
                <p className="text-slate-600 mt-1">Your input helps us make Kodo better for everyone.</p>
                
                {npsScore >= 8 && (
                  <div className="mt-6 p-4 bg-green-50 rounded-xl border border-green-100">
                    <p className="text-slate-700 mb-3">
                      We're thrilled you had a great experience! Would you mind sharing your thoughts on Trustpilot? 
                      It really helps others discover Kodo.
                    </p>
                    <Button
                      onClick={() => window.open('https://uk.trustpilot.com/review/getkodo.co.uk', '_blank')}
                      data-testid="trustpilot-review-button"
                      className="bg-[#00b67a] hover:bg-[#009967] text-white rounded-full px-6"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Leave a Review on Trustpilot
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <p className="text-slate-600 mb-4">How likely are you to recommend Kodo to a friend or colleague?</p>
                
                <div className="flex justify-between mb-2">
                  {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((score) => (
                    <button
                      key={score}
                      onClick={() => setNpsScore(score)}
                      data-testid={`nps-score-${score}`}
                      className={`w-9 h-9 rounded-lg font-semibold text-sm transition-all ${
                        npsScore === score
                          ? 'bg-primary text-white scale-110 shadow-md'
                          : score <= 6
                          ? 'bg-red-50 text-red-600 hover:bg-red-100'
                          : score <= 8
                          ? 'bg-yellow-50 text-yellow-600 hover:bg-yellow-100'
                          : 'bg-green-50 text-green-600 hover:bg-green-100'
                      }`}
                    >
                      {score}
                    </button>
                  ))}
                </div>
                
                <div className="flex justify-between text-xs text-slate-500 mb-4">
                  <span>Not at all likely</span>
                  <span>Extremely likely</span>
                </div>
                
                {npsScore !== null && (
                  <div className="space-y-3 animate-fade-in">
                    <textarea
                      value={npsComment}
                      onChange={(e) => setNpsComment(e.target.value)}
                      placeholder="What's the main reason for your score? (optional)"
                      className="w-full p-3 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                      rows={3}
                      data-testid="nps-comment"
                    />
                    <Button
                      onClick={submitNPS}
                      data-testid="nps-submit-button"
                      className="w-full rounded-xl"
                    >
                      Submit Feedback
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Back to Dashboard */}
          <div className="text-center pt-4">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              className="text-slate-500 hover:text-slate-700"
            >
              ← Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletionPage;
